import { memo, useState } from 'react';
import {
  File,
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Presentation,
  type LucideIcon,
} from 'lucide-react';
import { isImageFile, isPdfFile } from '@/utility/fileClassification';
import styles from './FileThumbnail.module.css';

export interface FileThumbnailProps {
  thumbnailUrl?: string | null;
  mimeType?: string | null;
  fileName?: string;
  sourceUrl?: string;
  className?: string;
}

type IconSpec = readonly [LucideIcon, string];

const IMAGE: IconSpec = [FileImage, styles.image];
const PDF: IconSpec = [FileText, styles.pdf];
const SHEET: IconSpec = [FileSpreadsheet, styles.sheet];
const DOC: IconSpec = [FileText, styles.doc];
const CODE: IconSpec = [FileCode, styles.code];
const ARCHIVE: IconSpec = [FileArchive, styles.archive];
const VIDEO: IconSpec = [FileVideo, styles.media];
const AUDIO: IconSpec = [FileAudio, styles.media];
const SLIDES: IconSpec = [Presentation, styles.media];
const PLAIN: IconSpec = [FileText, styles.default];
const UNKNOWN: IconSpec = [File, styles.default];

const EXTENSION_ICONS: Record<string, IconSpec> = {
  xls: SHEET,
  xlsx: SHEET,
  xlsm: SHEET,
  csv: SHEET,
  doc: DOC,
  docx: DOC,
  odt: DOC,
  rtf: DOC,
  txt: PLAIN,
  html: CODE,
  htm: CODE,
  json: CODE,
  xml: CODE,
  yml: CODE,
  yaml: CODE,
  md: CODE,
  zip: ARCHIVE,
  rar: ARCHIVE,
  '7z': ARCHIVE,
  tar: ARCHIVE,
  gz: ARCHIVE,
  ppt: SLIDES,
  pptx: SLIDES,
  mp4: VIDEO,
  mov: VIDEO,
  webm: VIDEO,
  mkv: VIDEO,
  avi: VIDEO,
  mp3: AUDIO,
  wav: AUDIO,
  ogg: AUDIO,
  m4a: AUDIO,
};

const MIME_ICONS: Record<string, IconSpec> = {
  'application/vnd.ms-excel': SHEET,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': SHEET,
  'application/vnd.ms-excel.sheet.macroEnabled.12': SHEET,
  'application/msword': DOC,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    DOC,
  'application/vnd.ms-powerpoint': SLIDES,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    SLIDES,
  'application/zip': ARCHIVE,
  'application/json': CODE,
  'text/html': CODE,
  'text/csv': SHEET,
  'text/plain': PLAIN,
};

const MIME_PREFIX_ICONS: ReadonlyArray<readonly [string, IconSpec]> = [
  ['video/', VIDEO],
  ['audio/', AUDIO],
  ['text/', PLAIN],
];

const EXTENSION_PATTERN = /\.([a-z0-9]+)(?:\?|$)/i;

function resolveIcon(
  mimeType?: string | null,
  fileName?: string,
  sourceUrl?: string
): IconSpec {

  const candidates = [fileName, sourceUrl];

  if (candidates.some((candidate) => isPdfFile(candidate, mimeType)))
    return PDF;
  if (candidates.some((candidate) => isImageFile(candidate, mimeType)))
    return IMAGE;

  if (mimeType) {
    const exact = MIME_ICONS[mimeType];
    if (exact) return exact;

    const prefixed = MIME_PREFIX_ICONS.find(([prefix]) =>
      mimeType.startsWith(prefix)
    );
    if (prefixed) return prefixed[1];
  }

  for (const candidate of candidates) {
    const extension = EXTENSION_PATTERN.exec(candidate ?? '')?.[1];
    const byExtension = extension && EXTENSION_ICONS[extension.toLowerCase()];
    if (byExtension) return byExtension;
  }

  return UNKNOWN;
}

const FileThumbnailBase = ({
  thumbnailUrl,
  mimeType,
  fileName,
  sourceUrl,
  className,
}: FileThumbnailProps) => {
  // Tracks the URL that failed rather than a boolean, so a refreshed signed URL
  // is retried instead of being stuck on the icon fallback for the row's life.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const wrapperClass = className
    ? `${styles.thumbnail} ${className}`
    : styles.thumbnail;

  if (thumbnailUrl && thumbnailUrl !== failedUrl) {
    return (
      <div className={wrapperClass}>
        <img
          src={thumbnailUrl}
          alt={fileName ?? ''}
          className={styles.preview}
          loading="lazy"
          decoding="async"
          onError={() => setFailedUrl(thumbnailUrl)}
        />
      </div>
    );
  }

  const [Icon, toneClass] = resolveIcon(mimeType, fileName, sourceUrl);

  return (
    <div className={wrapperClass}>
      <Icon className={`${styles.icon} ${toneClass}`} aria-hidden="true" />
    </div>
  );
};

export const FileThumbnail = memo(FileThumbnailBase);
